import mixpanel from 'mixpanel-browser';
import PropTypes from 'prop-types';
import React from 'react';

import { createInstance, SessionState, TerminalEvent, TerminalState } from '@fra.me/terminal-factory';

import * as consts from '../../constants';
import SpinnerMessage from '../SpinnerMessage';
import { Button } from '../UI';

const frameModes = {
  spinner: 'spinner',
  empty: 'empty',
  resume: 'resume',
  closing: 'closing',
};

const cookieName = 'currSesh=';

class FrameSession extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'Connecting to Cloud Service...',
      mode: frameModes.spinner,
      canStart: true,
      interval: null,
      terminal: null,
    };
  }

  componentDidMount() {
    // awaiting in this function caused weird issues,
    // so I'm using the completion callback instead
    this.createTerminal().then(() => {
      this.resumeSession();
    });

    // Setup unload event listener
    window.addEventListener('beforeunload', (ev) => {
      const { terminal } = this.state;
      if (terminal.state === TerminalState.RUNNING) {
        // Once we've created the frame terminal,
        // we should warn the user before the leave the tab
        ev.preventDefault();
        // To get Chrome to display the dialog, we have to set the
        // return value parameter. The message text doesn't seem to be displayed though
        const message = 'If you leave without closing your session, it will remain active for a short time before closing automatically';
        // eslint-disable-next-line no-param-reassign
        ev.returnValue = message;
        return message;
      }
      return null;
    });
  }

  componentWillUnmount() {
    this.cancelInterval();
  }

  createTerminal = async () => {
    // Time terminal creation
    mixpanel.time_event(consts.eventNames.createTerminal);

    const { authToken, launchpadId, setMode } = this.props;

    this.showSpinner('Establishing connection...');

    // Create the terminal and check for an active session
    let terminal = null;
    let message = '';
    try {
      terminal = await createInstance({
        serviceUrl: process.env.REACT_APP_SERVICE_URL,
        terminalConfigId: launchpadId,
        token: authToken,
      });
      this.setState({ terminal });

      terminal.bind(TerminalEvent.SESSION_CLOSING, this.updateSessionState);
      terminal.bind(TerminalEvent.SESSION_CLOSED, this.updateSessionState);
      terminal.bind(TerminalEvent.SESSION_DISCONNECTED, this.updateSessionState);
    } catch (err) {
      setMode(consts.modes.error, `Error connecting to the cloud: ${err.message}`);
      message = err.message;
    }

    // Update analytics
    mixpanel.track(consts.eventNames.createTerminal, {
      success: terminal != null,
      message,
    });
  }

  updateSessionState = async () => {
    const { setMode } = this.props;
    const { terminal, canStart } = this.state;

    if (terminal === null) { return; }

    const session = await terminal.getOpenSession();

    // If no session is active for the user
    if (session === null) {
      if (canStart) {
        // Try to start a new one if we haven't run a session yet
        this.startSession();
      } else {
        // Switch to the rating request component
        this.leaveToRating();
      }
    } else {
      // Depending on the session state
      switch (session.state) {
        case SessionState.OPEN:
          // Open means the user can resume a session if they want
          this.showResume();
          break;
        case SessionState.CLOSING:
          // As soon as closing starts, switch to the rating screen (it should appear above the frame session)
          this.leaveToRating();
          break;
        case SessionState.INIT:
          // Not quite sure atm when this happens,
          // hopefully we can wait for the session to go to open?
          this.showWaiting('Your session is initialising...');
          break;
        case SessionState.RESERVED:
          // Like init, not really sure when this is happening, so we'll just wait
          this.showWaiting('You have reserved your session...');
          break;
        case SessionState.CLOSED:
          // This shouldn't happen (as we shouldn't have an open session now)
          // but if it does, switch to the rating request component
          this.leaveToRating();
          break;
        default:
          // All remaining states show an error
          // It's possible this is overzealous
          setMode(consts.modes.error, `Cloud session is in an unexpected state: ${session.state}`);
          break;
      }
    }
  }

  startSession = async () => {
    this.showSpinner('Starting Cloud Session...');
    // Stop checking the session status if its running as
    // we should be kept informed via the terminal events
    this.cancelInterval();

    // Time starting the session for analytics
    mixpanel.time_event(consts.eventNames.startSession);

    const { appId, userData, setMode } = this.props;
    const { terminal } = this.state;
    let success = false;
    let message = '';

    try {
      // Tell frame to start
      const session = await terminal.start({ appId, userData });
      document.cookie = `${cookieName}${session.id}`;

      success = true;
      this.setState({ status: 'Cloud session is running...', canStart: false });
    } catch (err) {
      message = err.message;
      setMode(consts.modes.error, `Could not start a cloud session: ${message}`);
    }

    // Update analytics
    mixpanel.track(consts.eventNames.startSession, {
      success,
      message,
    });
  }

  resumeSession = async () => {
    const { setMode } = this.props;
    const { terminal } = this.state;

    if (terminal === null) return;

    const lastSession = await terminal.getOpenSession();

    // Last second check to make sure we can actually resume
    if (lastSession === null
        || lastSession.state !== SessionState.OPEN
        || !this.canResume(lastSession.id)) {
      this.updateSessionState();
      return;
    }

    this.showSpinner('Resuming Cloud Session...');
    // Stop checking the session status if its running as
    // we should be kept informed via the terminal events
    this.cancelInterval();

    // Tracking
    mixpanel.time_event(consts.eventNames.resumeSession);
    let success = false;
    let message = '';

    // Watch out for errors
    try {
      await terminal.resume(lastSession.id);
      success = true;
      this.setState({ status: 'Cloud session is running...', canStart: false });
    } catch (err) {
      // Update the user
      message = `Error resuming session: ${err.message}.`;
      setMode(consts.modes.error, `Could not resume existing session: ${err.message}`);
    }

    // Send analytic for resuming
    mixpanel.track(consts.eventNames.resumeSession, {
      success,
      message,
    });
  }

  showSpinner = (message) => {
    this.setState({
      status: message,
      mode: frameModes.spinner,
    });
  }

  canResume = (sessionId) => {
    const storedSessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith(cookieName))
      ?.split('=')[1];

    return storedSessionId === sessionId;
  }

  showResume = async () => {
    // Start checking the session state every second
    // We have to do this as after we get the disconnected
    // event, we won't receive any other session events
    this.startStatePolling();

    const { terminal } = this.state;
    const openSession = await terminal.getOpenSession();

    if (!this.canResume(openSession.id)) {
      this.showWaiting('Existing session open elsewhere, please close it to continue...');
    } else {
      this.setState({ mode: frameModes.resume });
    }
  }

  showWaiting = (message) => {
    // Start checking the session state every second
    // We have to do this as after we get the disconnected
    // event, we won't receive any other session events
    this.startStatePolling();

    this.setState({
      mode: frameModes.spinner,
      status: message,
    });
  }

  leaveToRating = () => {
    const { setMode } = this.props;

    // Delete the session cookie
    document.cookie = `${cookieName}; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;

    setMode(consts.modes.rating, '');
  }

  startStatePolling = () => {
    let { interval } = this.state;
    if (interval === null) {
      interval = setInterval(this.updateSessionState, 1000);
      this.setState({ interval });
    }
  }

  cancelInterval = () => {
    const { interval } = this.state;
    if (interval) {
      clearInterval(interval);
      this.setState({ interval: null });
    }
  }

  render() {
    const {
      mode,
      status,
    } = this.state;

    const { appName } = this.props;

    let title = `Welcome to ${appName} Cloud`;
    if (status === 'Your previous session is closing...') {
      title = `Thanks for using ${appName} Cloud`;
    }

    return (
      <div>
        <h3 className="app-title">
          {title}
        </h3>
        {{
          empty: null,

          spinner: (
            <SpinnerMessage message={status} />
          ),

          resume: (
            <div>
              <p className="app-status">You have an active session in the Cloud.</p>
              <p />
              <Button clicked={this.resumeSession}>
                Resume Session
              </Button>
            </div>
          ),
        }[mode]}
      </div>
    );
  }
}

FrameSession.propTypes = {
  authToken: PropTypes.string,
  launchpadId: PropTypes.string,
  appId: PropTypes.string,
  userData: PropTypes.string,
  setMode: PropTypes.func.isRequired,
  appName: PropTypes.string,
};

FrameSession.defaultProps = {
  authToken: null,
  launchpadId: '',
  appId: '',
  userData: '',
  appName: 'VU.CITY',
};

export default FrameSession;
